/**
 * The viewer remembers what it has shown (#1069 Story 8.3; FR83, NFR65).
 *
 * One `messageFileCache` per panel, held in a ref. The panel hands it to the
 * carousel's source (GitLab #1539): a hit paints at once; a miss goes through
 * `getOrFetch`; `downloadAttachmentFile` reads it with `peek` first (D5).
 * `useCorrespondence` knows nothing about file bytes and that stays so (C1).
 *
 * ⚠️ THREE CLEARS, AND THE `visible` ONE IS NOT BELT-AND-BRACES (C7). The
 * overlay panel is NEVER unmounted -- `CorrespondenceLauncher` toggles
 * `visible` -- so a ref-held store survives its close. Without the clear on
 * `visible` going false the overlay keeps up to ~107 MB of the last
 * conversation for as long as the request screen is open. The docked panel
 * does unmount, and its ref dies with it; the effect is what empties the one
 * that has no unmount. The thread-switch clear is the same event the panel's
 * gallery close sees; the `accessRevoked` clear is redundant with the thread
 * null the panel does on revocation and is pinned on its own because D3
 * depends on it: a revoked reader's cache is empty.
 *
 * ⚠️ THE PREFETCH CHAIN (8.3 D2) IS GONE (GitLab #1539, owner ruling
 * 2026-10-06). The carousel loads the file on screen and its two neighbours
 * through `useCarouselLazyLoad`, the same policy every request attachment
 * gallery has; the store only decides whether an ask costs a fetch. What
 * D2 bought -- the on-screen file first in line for the link -- is the
 * carousel's policy to change for every consumer, not this hook's.
 *
 * @see src/components/correspondence/__tests__/useMessageFileCache.test.ts
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { createMessageFileCache } from '../../services/correspondence/messageFileCache';
import { MESSAGE_FILE_CACHE_BYTES } from '../../services/correspondence/messageFileLimits';

import type { MessageFileCache } from '../../services/correspondence/messageFileCache';

export interface UseMessageFileCache {
  peek: MessageFileCache['peek'];
  getOrFetch: MessageFileCache['getOrFetch'];
  /** The load in flight for this id, if any, to wait on without inserting. */
  join: MessageFileCache['join'];
}

export const useMessageFileCache = ({
  openThreadId,
  visible,
  accessRevoked,
}: {
  openThreadId: string | number | null;
  visible: boolean;
  accessRevoked: boolean;
}): UseMessageFileCache => {
  const store = useRef<MessageFileCache | null>(null);
  if (store.current === null) {
    store.current = createMessageFileCache({
      ceilingBytes: MESSAGE_FILE_CACHE_BYTES,
    });
  }
  const cache = store.current;

  const clearAll = useCallback(() => {
    cache.clear();
  }, [cache]);

  // The three lifetimes (AC 6). Each is its own effect so each is its own
  // pin; see the header for why none of them is redundant.
  useEffect(() => {
    clearAll();
  }, [openThreadId, clearAll]);
  useEffect(() => {
    if (!visible) clearAll();
  }, [visible, clearAll]);
  useEffect(() => {
    if (accessRevoked) clearAll();
  }, [accessRevoked, clearAll]);

  const peek = useCallback<MessageFileCache['peek']>(
    fileId => cache.peek(fileId),
    [cache],
  );
  const getOrFetch = useCallback<MessageFileCache['getOrFetch']>(
    (fileId, loader, options) => cache.getOrFetch(fileId, loader, options),
    [cache],
  );
  const join = useCallback<MessageFileCache['join']>(
    fileId => cache.join(fileId),
    [cache],
  );

  // One object for the life of the panel, so the callbacks that list it as
  // a dependency (`gallerySource`, `downloadAttachmentFile`) stay stable.
  return useMemo(() => ({ peek, getOrFetch, join }), [peek, getOrFetch, join]);
};
